/**
 * AppLayout Component
 *
 * Main application layout with DogHeader and BottomNavigation.
 * Wraps all authenticated pages that need the standard app chrome.
 */

import { Outlet, useNavigate } from '@tanstack/react-router';
import { DogHeader } from '@/shared/components/home';
import { BottomNavigation } from '@/shared/components/layout/BottomNavigation';
import { useDogs, useDog } from '@/shared/hooks/useDogs';
import { useLastWeight } from '@/shared/hooks/useWeights';
import { useDogStore } from '@/shared/stores/dog-store';
import { Button } from '@/shared/components/common/Button';
import type { Dog } from '@/shared/types/api.types';

export function AppLayout() {
  const navigate = useNavigate();
  const { data: dogs, isLoading: isLoadingDogs } = useDogs();
  const { selectedDogId, setSelectedDogId } = useDogStore();
  const dogFromList = dogs?.find((dog) => dog.id === selectedDogId) ?? dogs?.[0];

  // Fetch individual dog data to ensure we have complete data (including avatarUrl)
  const { data: selectedDogData } = useDog(dogFromList?.id);

  // Fetch last weight for current dog
  const { data: lastWeight } = useLastWeight(dogFromList?.id);

  // Use individual dog data if available (has complete avatarUrl), fallback to list data
  const currentDog = selectedDogData ?? dogFromList;

  const handleDogSelect = (dog: Dog) => {
    setSelectedDogId(dog.id);
  };

  // Loading state
  if (isLoadingDogs) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // No dogs found - redirect to onboarding or show message
  if (!dogs || dogs.length === 0 || !currentDog) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Nenhum cachorro cadastrado</p>
          <Button onClick={() => void navigate({ to: '/onboarding' })}>
            Cadastrar Dog
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Fixed Header */}
      <DogHeader
        dogId={currentDog.id}
        dogName={currentDog.name}
        dogImage={currentDog.avatarUrl ?? ''}
        dogBirthDate={currentDog.birthDate}
        dogWeight={lastWeight?.weight}
        dogs={dogs}
        onDogSelect={handleDogSelect}
      />

      {/* Main Content - with padding for header and bottom nav */}
      <main className="pt-[72px] pb-24">
        <Outlet />
      </main>

      {/* Fixed Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
}
